import { ExecuteActionRequest, executeKeypadRequest, GameState, CheckVisitedRoomsRequest } from "@shared/types";
import { BaseRouteService } from "./BaseRouteService";
/**
 * Service to communicate with game routes of the server application
 */
export class GameRouteService extends BaseRouteService {
    /**
     * Get a type of game state for the current player from the server application
     *
     * @returns A type of game state for the current player
     */
    public async getGameState(): Promise<GameState> {
        return this.getJsonApi<GameState>("game/state");
    }

    /**
     * Execute an action for the current player on the server application
     *
     * @param actionAlias Alias of the action to execute
     * @param objectAliases Aliases of the game objects to execute the action on
     *
     * @returns A type of game state, or `undefined` if the action was unhandled. This can indicate the action requires a second game object to work.
     */
    public async executeAction(
        actionAlias: string,
        objectAliases?: string[]
    ): Promise<GameState | undefined> {
        try {
            return await this.postJsonApi<GameState, ExecuteActionRequest>("game/action", {
                action: actionAlias,
                objects: objectAliases,
            });
        }
        catch {
            return undefined;
        }
    }

    public async executeKeypad(
        lockId: string,
        inputCode: string
    ): Promise<GameState | undefined> {
        try {
            return await this.postJsonApi<GameState, executeKeypadRequest>("game/check-code", {
                lockId: lockId,
                inputCode: inputCode,
            });
        }
        catch {
            return undefined;
        }
    }

    /**
     * Retrieves the list of visited rooms
     *
     * @returns A Promise that resolves to an array of visited room aliases, or an empty array if the request fails.
     */
    public async getVisitedRooms(): Promise<string[]> {
        // fetch the visited rooms from the "game/visited-rooms" endpoint
        const response: CheckVisitedRoomsRequest = await this.getJsonApi<CheckVisitedRoomsRequest>("game/visited-rooms");
        // Return the response
        return response.visitedRooms;
    }

    /**
     * Sends the list of visited rooms to check which rooms are missing
     * @param visitedRooms An array of room aliases that the player has visited.
     * @returns A Promise that resolves to the server's response, or undefined if the request fails.
     */
    public async checkVisitedRooms(visitedRooms: string[]): Promise<CheckVisitedRoomsRequest | undefined> {
        try {
            // send a POST request to the "game/check-visited-rooms" endpoint The request body contains the 'visitedRooms' array.
            return await this.postJsonApi<CheckVisitedRoomsRequest, { visitedRooms: string[] }>("game/check-visited-rooms", {
                visitedRooms: visitedRooms,
            });
        }
        catch {
            // If an error occurs return undefined.
            return undefined;
        }
    }
}
